import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Users } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { key: "master", label: "Master" },
  { key: "rh_admin", label: "RH Administrador" },
  { key: "rh_operacional", label: "RH Operacional" },
  { key: "gestor_setor", label: "Gestor de Setor" },
  { key: "diretoria", label: "Diretoria" },
  { key: "consulta", label: "Consulta" },
];

const PERMS = {
  master: ["Acesso total", "Pode exceder quadro (senha master)", "Auditoria", "Redefinir permissões"],
  rh_admin: ["Cadastros RH", "Recrutamento", "Treinamentos", "Desligamentos", "Relatórios"],
  rh_operacional: ["Lançar movimentações", "Atualizar cadastros", "Registrar treinamentos", "Movimentar vagas"],
  gestor_setor: ["Ver seu setor", "Solicitar vagas", "Ver escalas/equipe"],
  diretoria: ["Dashboards executivos", "Relatórios consolidados", "Auditoria"],
  consulta: ["Somente leitura"],
};

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "consulta", password: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", { ...form, company_ids: [], sector_ids: [] });
      toast.success("Usuário criado");
      setOpen(false);
      setForm({ name: "", email: "", role: "consulta", password: "" });
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success("Status atualizado");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Administração</div>
          <h1 className="font-display text-3xl font-semibold">Usuários &amp; Perfis</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Gerencie acessos, perfis e permissões.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="user-new-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Novo usuário
        </Button>
      </div>

      {/* Role matrix */}
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#234935]" />
          <h3 className="font-display text-lg font-semibold">Perfis &amp; permissões</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {ROLES.map((r) => (
            <div key={r.key} className="rounded-md border border-border p-3">
              <div className="text-[13px] font-medium">{r.label}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">{r.key}</div>
              <ul className="mt-2 space-y-1 text-[12px] text-muted-foreground">
                {PERMS[r.key].map((p) => <li key={p}>· {p}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Users table */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Perfil</TableHead>
              <TableHead>Ativo</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-5" /></TableCell></TableRow>)
            ) : list.map((u) => (
              <TableRow key={u.id} className="rh-row" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={u.role === "master" ? "bg-[#F9F4E7] text-[#8A6B2B] border-[#E8D9AE]" : ""}>
                    {u.role_label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Switch checked={!!u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === user?.id} />
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">E-mail</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Senha</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={create} data-testid="user-save">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
